import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';

import { closeGenericModal } from '@actions/modal';
import { loading, loadingFailure, loadingSuccess } from '@actions/loading';
import {
  showConfirmationMessage,
  showErrorMessage,
  showSuccessMessage,
} from '@actions/messageconfirmation';

import { buildArraySchema } from '@commons/GenericForm';
import { CATEGORY_TYPES_OBJECT } from '@commons/constants/categoryTypes';
import { ENUM_MODULE_NAME } from '@commons/utils/features';
import { getChannelsTranslations } from '@commons/utils/translations';
import { isUserManager } from '@commons/utils/roles';
import AuditHistoryDrawer from '@commons/HistoryDrawer/AuditHistoryDrawer';
import GenericModal from '@commons/Modals/GenericModal';
import NavBar from '@commons/NavigationBar';

import { getClientBrandsWithoutNone, getClientInfo } from '@selectors/client';
import { getHasRecipeVariants } from '@selectors/featureProps';

/** Services **/
import { ingredient as ingredientService } from '@services/ingredient';
import centralService from '@services/central';
import clientService from '@services/client';
import recipeService from '@services/recipe';

import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';

import {
  buildFormFieldsFromComposition,
  formatCompositions,
  formatEntityWithChannels,
} from './components/RecipeComposition/utils/format';
import {
  Container,
  ContainerContent,
  GenericModalContainer,
  NavBarContainer,
  NavigationBar,
  NavigationPage,
  Section,
} from './styledComponents';
import { fetchRecipeParents, fetchRecipesByCategories } from './common/services';
import { isForbiddenAPIError } from '@commons/utils/errors';
import {
  RECIPES_COMPONENTS_FIELD_ARRAY_NAME,
  RECIPE_COMPONENTS_VALIDATION_INPUTS,
} from './components/RecipeComposition/utils/formInputsConfiguration';
import { sortCompositions } from './utils/sortCompositions';
import BottomBar from './components/BottomBar';
import Performances from '@admin/products/components/Performances';
import RecipeComposition from './components/RecipeComposition';
import RecipeInfoSection from './components/RecipeInfo';
import RecipeParentAssociations from './components/RecipeParentAssociations';
import RecipePictureSection from './components/RecipePictureSection';
import RecipePreparationSteps from './components/RecipePreparationSteps';
import RecipeProduction from './components/RecipeProduction';
import useAuditHistory from '@hooks/useAuditHistory';

import utilsMethods from './utils';

const RECIPE_ID_CREATE_MODE = 'create';
const RECIPE_ID_DUPLICATE_MODE = 'duplicate';
const EMPTY_RECIPE = {
  id: null,
  name: '',
  category: '',
  unit: '',
  supplierProducts: [],
  allergens: [],
  hasMultipleChannels: false,
  hasStock: false,
  batchProduction: false,
  hasLoss: true,
  hasTransfer: false,
  recipePreparationSteps: null,
  value: null,
  brands: [],
  inventoryListTemplates: [],
  categoryId: null,
};

const RECIPE_COMPOSITION_SUB_PAGE = {
  name: ({ compositions, recipeVariants, hasVariants }) => {
    if (hasVariants && recipeVariants.length > 0) {
      return i18next.t('ADMIN.RECIPES.RECIPE_WITH_VARIANT_COUNT', {
        count: recipeVariants.length + 1,
      });
    }

    return i18next.t('ADMIN.RECIPES.COMPONENT_WITH_COUNT', { count: compositions.length });
  },
  render: (props) => {
    const {
      allergens,
      clientId,
      handleCallToCreateIngredient,
      isReadOnly,
      recipe,
      recipesAvailable,
      setWarningModalParams,
      setIsSaveDisabled,
      compositions,
      clientChannelsWithTranslations,
      associatedProduct,
      isDuplicatingRecipe,
      recipeComponentsForm,
      formFields,
      addEntity,
      deleteEntity,
      fields,
      replace,
      isKitchen,
      recipeVariants,
      setRecipeVariants,
      setDeletedVariantIds,
      selectedVariantId,
      setSelectedVariantId,
    } = props;

    const selectedVariant = recipeVariants.find(({ id }) => id === selectedVariantId);
    const selectedComposition =
      selectedVariantId === null ? compositions : selectedVariant?.composition ?? [];

    return (
      <RecipeComposition
        addEntity={addEntity}
        allergens={allergens}
        associatedProduct={associatedProduct}
        channels={clientChannelsWithTranslations}
        clientId={clientId}
        compositions={selectedComposition}
        deleteEntity={deleteEntity}
        fields={fields}
        formFields={formFields}
        handleCallToCreateIngredient={handleCallToCreateIngredient}
        isDuplicatingRecipe={isDuplicatingRecipe}
        isKitchen={isKitchen}
        isReadOnly={isReadOnly}
        recipe={recipe}
        recipeComponentsForm={recipeComponentsForm}
        recipesAvailable={recipesAvailable}
        recipeVariants={recipeVariants}
        replace={replace}
        selectedVariantId={selectedVariantId}
        setDeletedVariantIds={setDeletedVariantIds}
        setIsSaveDisabled={setIsSaveDisabled}
        setRecipeVariants={setRecipeVariants}
        setSelectedVariantId={setSelectedVariantId}
        setWarningModalParams={setWarningModalParams}
      />
    );
  },
};

const RECIPE_PRODUCTION_SUB_PAGE = {
  name: ({ mappedStoreEntityMappings }) =>
    i18next.t('ADMIN.RECIPES.PRODUCTION_WITH_COUNT', { count: mappedStoreEntityMappings.length }),
  render: (props) => {
    const {
      recipe,
      setWarningModalParams,
      associatedProduct,
      mappedStoreEntityMappings,
      setMappedStoreEntityMappings,
      notMappedStoreEntityMappings,
      setNotMappedStoreEntityMappings,
      selectedStoreEntityMappings,
      setSelectedStoreEntityMappings,
    } = props;

    return (
      <RecipeProduction
        associatedProduct={associatedProduct}
        mappedStoreEntityMappings={mappedStoreEntityMappings}
        notMappedStoreEntityMappings={notMappedStoreEntityMappings}
        recipe={recipe}
        selectedStoreEntityMappings={selectedStoreEntityMappings}
        setMappedStoreEntityMappings={setMappedStoreEntityMappings}
        setNotMappedStoreEntityMappings={setNotMappedStoreEntityMappings}
        setSelectedStoreEntityMappings={setSelectedStoreEntityMappings}
        setWarningModalParams={setWarningModalParams}
      />
    );
  },
};

const RECIPE_PREPARATION_STEPS_SUB_PAGE = {
  name: () => i18next.t('ADMIN.RECIPES.PREPARATION_STEPS'),
  render: (props) => {
    const { recipe, compositions, allergens, onRecipeChange } = props;

    return (
      <RecipePreparationSteps
        allergens={allergens}
        compositions={compositions}
        recipe={recipe}
        onRecipeChange={onRecipeChange}
      />
    );
  },
};

const RECIPE_PARENTS_SUB_PAGE = {
  name: ({ entitiesMapped }) =>
    `${i18next.t('ADMIN.RECIPES.ELEMENTS_USING_THIS_RECIPE')} (${entitiesMapped.length})`,
  render: (props) => (
    <RecipeParentAssociations
      entitiesMapped={props.entitiesMapped}
      recipe={props.recipe}
      showMessage={props.showMessage}
      onEntitiesMappedChange={props.onEntitiesMappedChange}
    />
  ),
};

const RECIPE_PERFORMANCES_SUB_PAGE = {
  name: () => i18next.t('ADMIN.RECIPES.PERFORMANCES'),
  render: (props) => {
    const {
      associatedProduct,
      clientChannelsWithTranslations,
      compositions,
      currency,
      defaultChannelId,
      recipe,
      recipeVariants,
      stores,
    } = props;

    return (
      <Performances
        associatedProduct={associatedProduct}
        clientChannels={clientChannelsWithTranslations}
        compositions={compositions}
        currency={currency}
        defaultChannelId={defaultChannelId}
        emptyStateLabel={i18next.t('ADMIN.RECIPES.PERFORMANCES_NO_PRODUCT_EMPTY_STATE')}
        isEmptyState={isEmpty(associatedProduct)}
        recipe={recipe}
        recipeVariants={recipeVariants}
        stores={stores}
      />
    );
  },
};

export function renderSectionRecipeSubPages(props) {
  const {
    activeSubPages,
    entitiesMapped,
    compositions,
    mappedStoreEntityMappings,
    recipeVariants,
    hasVariants,
  } = props;

  return (
    <>
      {activeSubPages.length > 1 && (
        <Section>
          <NavigationBar>
            {activeSubPages.map((page, indexPage) => (
              <NavigationPage
                key={indexPage}
                selected={props.selectedRecipeSubPage.name === page.name}
                onClick={() => props.onSelectedRecipeSubPage(page)}
              >
                {page.name({
                  compositions,
                  entitiesMapped,
                  mappedStoreEntityMappings,
                  recipeVariants,
                  hasVariants,
                })}
              </NavigationPage>
            ))}
          </NavigationBar>
        </Section>
      )}

      <Section nbSubPages={props.activeSubPages.length} sectionType="subpage">
        {props.selectedRecipeSubPage.render(props)}
      </Section>
    </>
  );
}

export function renderContent(props) {
  const path = get(props, 'match.path');

  const {
    recipe,
    history,
    isReadOnly,
    onRecipeChange,
    allergens,
    recipeCategories,
    recipeCost,
    recipeId,
    warningModalParams,
    setWarningModalParams,
    isSaveDisabled,
    compositions,
    recipeComponentsForm,
    clientChannelsWithTranslations,
    client: { hasMultipleChannels, hasMultipleBrands },
    isKitchen,
    mappedStoreEntityMappings,
    initialMappedStoreEntityMappings,
    initialCompositions,
    initialRecipe,
    brands,
    inventoryListTemplates,
    setRecipeCategories,
    setInventoryListTemplates,
    recipeVariants,
    initialRecipeVariants,
    deletedVariantIds,
    selectedVariantId,
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  } = props;

  return (
    <>
      <NavBarContainer>
        <NavBar
          bigTopBar={true}
          module={
            isKitchen ? ENUM_MODULE_NAME.KITCHEN_RECIPE_DETAIL : ENUM_MODULE_NAME.RECIPE_DETAIL
          }
          path={path}
          recipe={recipe}
        />
        {recipeComponentsForm && (
          <BottomBar
            auditButtonLabel={auditButtonLabel}
            auditButtonLoading={auditButtonLoading}
            channels={clientChannelsWithTranslations}
            clientHasMultipleBrands={hasMultipleBrands}
            clientHasMultipleChannels={hasMultipleChannels}
            compositions={compositions}
            deletedVariantIds={deletedVariantIds}
            history={history}
            initialCompositions={initialCompositions}
            initialProductionStoreEntityMappings={initialMappedStoreEntityMappings}
            initialRecipe={initialRecipe}
            initialRecipeVariants={initialRecipeVariants}
            isReadOnly={isReadOnly}
            isSaveDisabled={isSaveDisabled}
            productionStoreEntityMappings={mappedStoreEntityMappings}
            recipe={recipe}
            recipeComponentsForm={recipeComponentsForm}
            recipeVariants={recipeVariants}
            selectedVariantId={selectedVariantId}
            onOpenAuditDrawer={onOpenAuditDrawer}
            onRecipeChange={onRecipeChange}
          />
        )}
      </NavBarContainer>
      <ContainerContent>
        <Section>
          <RecipeInfoSection
            allergens={allergens}
            brands={brands}
            clientHasMultipleBrands={hasMultipleBrands}
            clientHasMultipleChannels={hasMultipleChannels}
            inventoryListTemplates={inventoryListTemplates}
            isKitchen={isKitchen}
            isReadOnly={isReadOnly}
            recipe={recipe}
            recipeCategories={recipeCategories}
            recipeCost={recipeCost}
            recipeId={recipeId}
            setInventoryListTemplates={setInventoryListTemplates}
            setRecipeCategories={setRecipeCategories}
            onRecipeChange={onRecipeChange}
          />
          <RecipePictureSection
            isReadOnly={isReadOnly}
            recipe={recipe}
            onRecipeChange={onRecipeChange}
          />
        </Section>
        {renderSectionRecipeSubPages(props)}
      </ContainerContent>
      {warningModalParams && (
        <GenericModalContainer>
          <GenericModal
            closeGenericModal={() => setWarningModalParams(null)}
            component={warningModalParams.component}
            params={warningModalParams}
          />
        </GenericModalContainer>
      )}
    </>
  );
}

export const clearState = (
  onEntitiesMappedChange,
  onActiveSubPageChange,
  onSelectedRecipeSubPage,
  setRecipeVariants,
  setSelectedVariantId,
  setInitialRecipeVariants,
  setDeletedVariantIds,
) => {
  onEntitiesMappedChange(false);
  onActiveSubPageChange([RECIPE_COMPOSITION_SUB_PAGE, RECIPE_PREPARATION_STEPS_SUB_PAGE]);
  onSelectedRecipeSubPage(RECIPE_COMPOSITION_SUB_PAGE);
  setRecipeVariants([]);
  setSelectedVariantId(null);
  setInitialRecipeVariants([]);
  setDeletedVariantIds([]);
};

export const RecipeDetails = (props) => {
  const {
    match: { params },
    client: { defaultChannelId, hasMultipleChannels, hasMultipleBrands, clientChannels },
    location,
    user,
    history,
    pageLoaded,
    pageLoading,
    pageDidNotLoad,
    showErrorMessage,
    closeGenericModal,
    showSuccessMessage,
    clientBrands,
    hasVariants,
  } = props;

  const clientChannelsWithTranslations = getChannelsTranslations(clientChannels);

  const recipeId = params.id;
  const clientId = get(user, 'clientId');
  const isDuplicatingRecipe =
    location && location.type === RECIPE_ID_DUPLICATE_MODE && !!location.recipe;

  const [isDuplication, onDuplicationChange] = useState(false);

  const [recipe, onRecipeChange] = useState(EMPTY_RECIPE);

  const {
    auditLogs,
    auditButtonLabel,
    auditButtonLoading,
    isAuditDrawerOpened,
    fetchAuditHistory,
    toggleAuditDrawer,
    closeAuditDrawer,
  } = useAuditHistory({ showErrorMessage, targetId: recipe?.id });

  const [initialRecipe, setInitialRecipe] = useState(EMPTY_RECIPE);
  const [initialCompositions, setInitialCompositions] = useState([]);

  const [recipeCost, onRecipeCostChange] = useState(null);
  const [compositions, setCompositions] = useState([]);
  const [allergens, onAllergensChange] = useState([]);

  const [recipesAvailable, onRecipesAvailableChange] = useState([]);
  const [recipeCategories, setRecipeCategories] = useState([]);

  const [inventoryListTemplates, setInventoryListTemplates] = useState([]);

  const [entitiesMapped, onEntitiesMappedChange] = useState(false);
  const [activeSubPages, onActiveSubPageChange] = useState([
    RECIPE_COMPOSITION_SUB_PAGE,
    RECIPE_PREPARATION_STEPS_SUB_PAGE,
  ]);
  const [selectedRecipeSubPage, onSelectedRecipeSubPage] = useState(RECIPE_COMPOSITION_SUB_PAGE);

  const [warningModalParams, setWarningModalParams] = useState(null);

  const [isLoading, setLoading] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [associatedProduct, setAssociatedProduct] = useState({});

  const [recipeVariants, setRecipeVariants] = useState([]);
  const [initialRecipeVariants, setInitialRecipeVariants] = useState([]);
  const [deletedVariantIds, setDeletedVariantIds] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  // Lets the swap effect tell a real tab change from an unrelated re-render.
  const previousSelectedVariantIdRef = useRef(null);

  // Always-current refs so the formFields effect reads the latest values without
  // listing them as deps (adding them would cause a double-run on tab change).
  const selectedVariantIdRef = useRef(selectedVariantId);
  selectedVariantIdRef.current = selectedVariantId;

  const recipeVariantsRef = useRef(recipeVariants);
  recipeVariantsRef.current = recipeVariants;

  // Used by Central Kitchen Production sub-page
  const [mappedStoreEntityMappings, setMappedStoreEntityMappings] = useState([]);
  const [initialMappedStoreEntityMappings, setInitialMappedStoreEntityMappings] = useState([]);
  const [notMappedStoreEntityMappings, setNotMappedStoreEntityMappings] = useState([]);
  const [selectedStoreEntityMappings, setSelectedStoreEntityMappings] = useState([]);

  const isKitchen = getIsInCentralKitchenOnlyPage();

  const recipeComponentsForm = useForm({
    mode: 'all', // To make sure that validation is run on every action
    defaultValues: {
      [RECIPES_COMPONENTS_FIELD_ARRAY_NAME]: [],
    },
    resolver: yupResolver(
      buildArraySchema(RECIPES_COMPONENTS_FIELD_ARRAY_NAME, RECIPE_COMPONENTS_VALIDATION_INPUTS),
    ),
  });

  const { fields, prepend, remove, replace } = useFieldArray({
    control: recipeComponentsForm.control,
    name: RECIPES_COMPONENTS_FIELD_ARRAY_NAME,
    defaultValues: {
      [RECIPES_COMPONENTS_FIELD_ARRAY_NAME]: [],
    },
  });

  const formFields = useWatch({
    name: RECIPES_COMPONENTS_FIELD_ARRAY_NAME,
    control: recipeComponentsForm.control,
  });

  const handleCentralKitchenRecipeStoreEntityMappings = async (recipeId) => {
    const { stores } = props;

    const centralKitchenStores = stores.filter(({ isKitchen }) => isKitchen);

    const storeEntityMappings =
      await centralService.getCentralKitchenRecipeProductionMappingsByEntityId(recipeId);

    const formattedStoreEntityMappings = storeEntityMappings.map(
      ({ id, storeId, entityId, lnkStoreStoreEntityMappingrel }) => ({
        id,
        storeId,
        entityId,
        storeName: get(lnkStoreStoreEntityMappingrel, 'name', ''),
        storeCity: get(lnkStoreStoreEntityMappingrel, 'city', ''),
        storeCountry: get(lnkStoreStoreEntityMappingrel, 'country', ''),
      }),
    );

    setMappedStoreEntityMappings(formattedStoreEntityMappings);
    setInitialMappedStoreEntityMappings(formattedStoreEntityMappings);

    const storeEntityMappingsStoreIds = formattedStoreEntityMappings.map(({ storeId }) => storeId);

    const availableStoreEntityMappingToBeAdd = centralKitchenStores.filter(
      ({ id }) => !storeEntityMappingsStoreIds.includes(id),
    );

    const formattedAvailableMappings = availableStoreEntityMappingToBeAdd.map(
      ({ id, name, city, country }) => ({
        entityId: recipeId,
        storeId: id,
        storeName: name,
        storeCity: city,
        storeCountry: country,
      }),
    );

    setNotMappedStoreEntityMappings(formattedAvailableMappings);
  };

  const handleFetchInventoryTemplates = async () => {
    const result = await clientService.getInventoryListTemplates({
      withMappings: false,
      filterByUserCatalog: true,
    });

    setInventoryListTemplates(result);
  };

  useEffect(() => {
    pageLoading();

    clearState(
      onEntitiesMappedChange,
      onActiveSubPageChange,
      onSelectedRecipeSubPage,
      setRecipeVariants,
      setSelectedVariantId,
      setInitialRecipeVariants,
      setDeletedVariantIds,
    );

    previousSelectedVariantIdRef.current = null;

    fetchRecipesByCategories(clientId, onRecipesAvailableChange, setRecipeCategories, isKitchen);

    handleFetchInventoryTemplates();

    (async function loadData() {
      try {
        if (!recipeId || recipeId === RECIPE_ID_CREATE_MODE) {
          if (isDuplicatingRecipe) {
            const [{ recipe: fetchedRecipe, product }, fetchedCompositions] = await Promise.all([
              recipeService.getRecipe(location.recipe.id, false),
              recipeService.getCompositions(location.recipe.id),
            ]);

            sortCompositions(fetchedCompositions);

            setAssociatedProduct(product || {});
            setCompositions(fetchedCompositions);
            setInitialCompositions(fetchedCompositions);
            setInitialRecipe(fetchedRecipe);
            onRecipeChange(fetchedRecipe);
            onDuplicationChange(true);

            pageLoaded();
            return;
          }

          if (hasMultipleChannels) {
            setInitialRecipe({ ...recipe, hasMultipleChannels: isKitchen ? false : true });
            onRecipeChange({ ...initialRecipe, hasMultipleChannels: isKitchen ? false : true });
          }
        } else {
          const subPages = isKitchen
            ? [
                RECIPE_COMPOSITION_SUB_PAGE,
                RECIPE_PERFORMANCES_SUB_PAGE,
                RECIPE_PREPARATION_STEPS_SUB_PAGE,
                RECIPE_PRODUCTION_SUB_PAGE,
                RECIPE_PARENTS_SUB_PAGE,
              ]
            : [
                RECIPE_COMPOSITION_SUB_PAGE,
                RECIPE_PERFORMANCES_SUB_PAGE,
                RECIPE_PREPARATION_STEPS_SUB_PAGE,
                RECIPE_PARENTS_SUB_PAGE,
              ];
          onActiveSubPageChange(subPages);

          fetchRecipeParents(recipeId, onEntitiesMappedChange);

          const [
            { recipe: fetchedRecipe, product },
            fetchedCompositions,
            { recipeVariants: fetchedVariants },
          ] = await Promise.all([
            recipeService.getRecipe(recipeId, false),
            recipeService.getCompositions(recipeId),
            hasVariants
              ? recipeService.getRecipeVariants(recipeId)
              : Promise.resolve({ recipeVariants: [] }),
          ]);

          sortCompositions(fetchedCompositions);

          setAssociatedProduct(product || {});
          setCompositions(fetchedCompositions);
          setInitialCompositions(fetchedCompositions);
          setInitialRecipe(fetchedRecipe);
          onRecipeChange(fetchedRecipe);

          fetchAuditHistory(recipeId);

          if (hasVariants) {
            setRecipeVariants(fetchedVariants);
            setInitialRecipeVariants(fetchedVariants);
          }

          if (isKitchen && recipeId) {
            await handleCentralKitchenRecipeStoreEntityMappings(recipeId);
          }
        }
        pageLoaded();
      } catch (error) {
        showErrorMessage(i18next.t('ADMIN.RECIPES.LOAD_DETAIL_FAILED'));
        pageDidNotLoad();

        if (isForbiddenAPIError) {
          history.goBack();
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [recipeId]);

  useEffect(() => {
    const { recipeComponents: formEntities } = recipeComponentsForm.getValues();

    // Persist the recomputed composition to the selected tab's slot so variant data stays the
    // live source — Performances and the save button reflect unsaved edits.
    const currentSelectedVariantId = selectedVariantIdRef.current;
    const selectedVariant = recipeVariantsRef.current.find(
      ({ id }) => id === currentSelectedVariantId,
    );
    const referenceComposition =
      currentSelectedVariantId === null ? compositions : selectedVariant?.composition ?? [];

    const formattedComposition = formatCompositions(
      formEntities,
      referenceComposition,
      clientChannelsWithTranslations,
    );

    const formattedCostCompositions =
      utilsMethods.formatCompositionsEntityCost(formattedComposition);

    if (currentSelectedVariantId === null) {
      setCompositions(formattedCostCompositions);
    } else {
      setRecipeVariants((currentVariants) =>
        currentVariants.map((variant) =>
          variant.id === currentSelectedVariantId
            ? { ...variant, composition: formattedCostCompositions }
            : variant,
        ),
      );
    }

    onRecipeCostChange(utilsMethods.getRecipeCost(formattedCostCompositions));

    (async () => {
      const fieldsValidation = await recipeComponentsForm.trigger();
      setIsSaveDisabled(!fieldsValidation);
    })();
  }, [formFields]);

  // On a tab change, load the target tab's composition into the shared form (the leaving tab is
  // already kept in sync by the effect above).
  useEffect(() => {
    if (previousSelectedVariantIdRef.current === selectedVariantId) {
      return;
    }

    previousSelectedVariantIdRef.current = selectedVariantId;

    const selectedVariant = recipeVariants.find(({ id }) => id === selectedVariantId);
    const targetComposition =
      selectedVariantId === null ? compositions : selectedVariant?.composition ?? [];

    replace(buildFormFieldsFromComposition(targetComposition, clientChannelsWithTranslations));
  }, [selectedVariantId]);

  useEffect(() => {
    if (isDuplication) {
      onRecipeChange({
        ...recipe,
        id: null,
        name: i18next.t('GENERAL.CLONE_NAME', { name: recipe.name }),
      });
    }
  }, [isDuplication]);

  useEffect(() => {
    if (!initialRecipe.hasMultipleChannels && recipe.hasMultipleChannels) {
      const { recipeComponents: currentValues } = recipeComponentsForm.getValues();

      const updatedCompositions = currentValues.map((value) => {
        const updatedChannels = { ...value.channels };

        clientChannelsWithTranslations.forEach((channel) => {
          updatedChannels[channel.id] = true;
        });

        return { ...value, channels: updatedChannels };
      });

      replace(updatedCompositions);
    }
  }, [recipe.hasMultipleChannels]);

  useEffect(() => {
    const allCompositions = [
      ...compositions,
      ...recipeVariants.flatMap(({ composition = [] }) => composition),
    ];

    const allergensById = new Map();

    allCompositions.forEach(({ allergens: lineAllergens = [] }) => {
      lineAllergens.forEach((allergen) => {
        if (allergen?.id && !allergensById.has(allergen.id)) {
          allergensById.set(allergen.id, allergen);
        }
      });
    });

    onAllergensChange(Array.from(allergensById.values()));
  }, [compositions, recipeVariants]);

  const isReadOnly = isUserManager(user) || (!recipe.active && recipe.id);

  if (isLoading) {
    return null;
  }

  const handleCallToCreateIngredient = async (ingredient) => {
    pageLoading();

    try {
      let ingredientCategoryId = ingredient.categoryId;

      if (!ingredientCategoryId) {
        const { categories } = await clientService.getCategoriesAndSubCategories(
          CATEGORY_TYPES_OBJECT.INGREDIENT,
        );

        const categoryNone = categories.find(({ name }) => !name);

        ingredientCategoryId = categoryNone.id;
      }

      const newlyCreatedIngredient = await ingredientService.createIngredient(clientId, {
        name: ingredient.name,
        unit: ingredient.unit,
        category: ingredient.category,
        code: ingredient.code,
        cost: ingredient.cost,
        supplierProducts: ingredient.supplierProducts.map(({ id, isUsedForCost }) => ({
          id,
          isUsedForCost,
        })),
        allergenIds: ingredient.allergens.map((allergen) => allergen.id),
        isStrategic: ingredient.isStrategic,
        categoryId: ingredientCategoryId,
      });

      showSuccessMessage(i18next.t('ADMIN.INGREDIENTS.CREATION_SUCCESS'));

      const defaultChannel = clientChannelsWithTranslations.find(
        ({ id }) => id === defaultChannelId,
      );

      const composition = {
        allergens: ingredient.allergens,
        entity: newlyCreatedIngredient,
        quantity: 0,
        cost: ingredient.cost,
        channels: recipe.hasMultipleChannels ? clientChannelsWithTranslations : [defaultChannel],
      };

      prepend(formatEntityWithChannels(composition, clientChannelsWithTranslations));
      recipeComponentsForm.trigger(RECIPES_COMPONENTS_FIELD_ARRAY_NAME); // Force validate added item to render proper icon status

      await fetchRecipesByCategories(
        clientId,
        onRecipesAvailableChange,
        setRecipeCategories,
        isKitchen,
      );

      closeGenericModal();
    } catch {
      showErrorMessage(i18next.t('ADMIN.INGREDIENTS.CREATION_ERROR'));
    }

    pageLoaded();
  };

  const addEntity = async (item) => {
    prepend({ ...item, quantity: 1 });

    recipeComponentsForm.trigger(RECIPES_COMPONENTS_FIELD_ARRAY_NAME); // Force validate added item to render proper icon status
  };

  const deleteEntity = (index, field) => {
    remove(index);
    recipeComponentsForm.trigger(RECIPES_COMPONENTS_FIELD_ARRAY_NAME);
  };

  return (
    <Container>
      {renderContent({
        ...props,
        defaultChannelId,
        recipe,
        recipeCost,
        activeSubPages,
        selectedRecipeSubPage,
        onSelectedRecipeSubPage,
        onRecipeChange,
        recipeCategories,
        isDuplication,
        recipesAvailable,
        allergens,
        isReadOnly,
        entitiesMapped,
        onEntitiesMappedChange,
        clientId,
        handleCallToCreateIngredient,
        warningModalParams,
        setWarningModalParams,
        isSaveDisabled,
        setIsSaveDisabled,
        compositions,
        setCompositions,
        initialRecipe,
        clientChannelsWithTranslations,
        associatedProduct,
        isDuplicatingRecipe,
        isKitchen,
        mappedStoreEntityMappings,
        initialMappedStoreEntityMappings,
        setMappedStoreEntityMappings,
        notMappedStoreEntityMappings,
        setNotMappedStoreEntityMappings,
        selectedStoreEntityMappings,
        setSelectedStoreEntityMappings,
        recipeComponentsForm,
        formFields,
        addEntity,
        deleteEntity,
        fields,
        replace,
        initialCompositions,
        brands: hasMultipleBrands ? clientBrands : [],
        inventoryListTemplates,
        setRecipeCategories,
        setInventoryListTemplates,
        hasVariants,
        recipeVariants,
        setRecipeVariants,
        initialRecipeVariants,
        deletedVariantIds,
        setDeletedVariantIds,
        selectedVariantId,
        setSelectedVariantId,
        auditButtonLabel,
        auditButtonLoading,
        onOpenAuditDrawer: toggleAuditDrawer,
      })}
      <AuditHistoryDrawer
        auditLogs={auditLogs}
        isOpened={isAuditDrawerOpened}
        subtitle={recipe.name}
        onClose={closeAuditDrawer}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
  userRights: state.baseReducer.userRights,
  client: getClientInfo(state.baseReducer.user),
  stores: state.baseReducer.activeStores,
  currency: state.baseReducer.currency,
  clientBrands: getClientBrandsWithoutNone(state.baseReducer.brands),
  hasVariants: getHasRecipeVariants(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  pageDidNotLoad: () => {
    dispatch(loadingFailure());
  },
  closeGenericModal: (params) => {
    dispatch(closeGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RecipeDetails);
